import { type RefObject, useEffect, useRef, useState } from "react";

const COMPACT_HEADER_REVEAL_GAP = 32;
const COMPACT_HEADER_VISIBILITY_HYSTERESIS = 8;

/** 通常ヘッダー全体が見えなくなり、共通の間隔を進んだ後で同じ要素を固定する。 */
export function useFixedWorkspaceControl(
  controlRef: RefObject<HTMLElement | null>,
) {
  const thresholdRef = useRef(0);
  const [isFixed, setIsFixed] = useState(false);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);
  const [fixedStyle, setFixedStyle] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    const control = controlRef.current;
    const main = control?.closest("main");
    if (!control || !main) return;

    const measureFixedStyle = () => {
      const { top, left, width } = main.getBoundingClientRect();
      setFixedStyle((current) =>
        current.top === top && current.left === left && current.width === width
          ? current
          : { top, left, width },
      );
    };
    const updateFixedState = () => {
      // 境界付近の微小な逆スクロールや小数丸めで表示が往復しないよう、解除側だけ余裕を持たせる。
      const threshold =
        thresholdRef.current -
        (isFixed ? COMPACT_HEADER_VISIBILITY_HYSTERESIS : 0);
      const nextIsFixed = main.scrollTop >= threshold;
      if (nextIsFixed === isFixed) return;
      if (nextIsFixed) measureFixedStyle();
      setIsFixed(nextIsFixed);
    };
    const measureExpanded = () => {
      if (control.hasAttribute("data-sticky")) return;
      const mainTop = main.getBoundingClientRect().top;
      const controlRect = control.getBoundingClientRect();
      const marginTop = Number.parseFloat(getComputedStyle(control).marginTop);
      thresholdRef.current =
        main.scrollTop +
        controlRect.bottom -
        mainTop +
        COMPACT_HEADER_REVEAL_GAP;
      // 固定化で負の margin が外れても、後続コンテンツの位置を変えない。
      setPlaceholderHeight(controlRect.height + (marginTop || 0));
      updateFixedState();
    };

    const observer = new ResizeObserver(
      isFixed ? measureFixedStyle : measureExpanded,
    );
    if (isFixed) {
      measureFixedStyle();
      observer.observe(main, { box: "border-box" });
    } else {
      measureExpanded();
      observer.observe(control, { box: "border-box" });
    }
    main.addEventListener("scroll", updateFixedState, { passive: true });
    return () => {
      observer.disconnect();
      main.removeEventListener("scroll", updateFixedState);
    };
  }, [controlRef, isFixed]);

  return { isFixed, placeholderHeight, fixedStyle };
}
